
import React, { useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function Settings() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const { user } = useAuth();
  const { toast } = useToast();

  const validatePasswords = () => {
    if (newPassword.length < 6) {
      setPasswordError("Wachtwoord moet minimaal 6 tekens bevatten");
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError("Wachtwoorden komen niet overeen");
      return false;
    }
    
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast({
          title: "Fout bij wijzigen wachtwoord",
          description: error.message,
          variant: "destructive",
        });
        console.error("Wachtwoord update error:", error);
      } else {
        toast({
          title: "Wachtwoord gewijzigd",
          description: "Je wachtwoord is succesvol gewijzigd",
        });
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error: any) {
      console.error("Onverwachte fout bij wachtwoord update:", error);
      toast({
        title: "Fout bij wijzigen wachtwoord",
        description: "Er is iets misgegaan bij het wijzigen van je wachtwoord",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-12 px-6">
        <div className="flex items-center mb-8">
          <Button variant="ghost" asChild className="p-0 mr-3 hover:bg-transparent">
            <Link to="/" className="flex items-center text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5 mr-1" />
              Terug naar hoofdpagina
            </Link>
          </Button>
          <h1 className="text-4xl font-bold">Instellingen</h1>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Wijzig wachtwoord</CardTitle>
            <CardDescription>
              Gebruik dit formulier om je wachtwoord te wijzigen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="new-password" className="text-base font-medium">
                  Nieuw wachtwoord
                </label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Voer je nieuwe wachtwoord in"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm-password" className="text-base font-medium">
                  Bevestig nieuw wachtwoord
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Bevestig je nieuwe wachtwoord"
                />
                {passwordError && (
                  <p className="text-sm text-destructive">{passwordError}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="bg-green-500 hover:bg-green-600 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Wachtwoord wijzigen..." : "Wachtwoord wijzigen"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center text-muted-foreground text-sm">
          Made with <span className="text-green-500">💚</span> by{" "}
          <a 
            href="https://beterturnen.nl/turnles-planner" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline hover:text-primary transition-colors"
          >
            Beter Turnen
          </a>
        </div>
      </div>
    </Layout>
  );
}
